/* Chill Bunny — Quote Request modal (Shopify embed).
 *
 * Vanilla JS, no dependencies. Pairs with snippets/quote-request-modal.liquid.
 * Opens from any [data-cbqr-open] element, traps focus, persists a half-filled
 * form in sessionStorage, validates inline on blur, and posts the submission to
 * the Hutch API's open lead-ingest endpoint (POST /leads/ingest, X-Api-Key).
 *
 * Multi-product aware: the modal is a single shared instance, and each trigger
 * carries the product it opens for via  data-cbqr-open="<product.id>". The
 * product context (title/image/variants) comes from a matching
 * <script data-cbqr-product="<id>"> block, so one modal serves every product on
 * a category/collection page — not just the first. A bare data-cbqr-open (no
 * value) resolves to the only product block on the page (the product-page case).
 *
 * The payload is shaped for Hutch's leadIngestSchema: known columns (contact,
 * attribution, product_interest) map onto the leads table directly, and the
 * full structured quote request rides along as `quote_request` — the schema is
 * passthrough and ingest stores the raw body untouched in raw_lead_payload, so
 * nothing is lost for the later deal-automation pass.
 *
 * Idempotency: each form fill gets a stable submission id (sessionStorage),
 * sent as external_id. Ingest upserts on external_id, so a retry after a
 * network error — or any double-submit — refreshes the same lead instead of
 * duplicating it. The id rotates only after a confirmed success.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cbqr-root]');
  if (!root) return;

  var config = readJson('[data-cbqr-config]');
  if (!config || !config.endpoint) return;

  // ── Product catalogue on the page (keyed by product id) ────────────────────
  // Each [data-cbqr-product="<id>"] block is { product: {...}, variants: [...] }.
  // On a product page there's one; on a category page there's one per card.
  var products = {};
  var defaultProductId = null;
  Array.prototype.forEach.call(document.querySelectorAll('[data-cbqr-product]'), function (el) {
    var data;
    try { data = JSON.parse(el.textContent); } catch (e) { return; }
    if (!data || !data.product) return;
    var id = el.getAttribute('data-cbqr-product');
    products[id] = { product: data.product, variants: data.variants || [] };
    if (defaultProductId === null) defaultProductId = id;
  });
  if (defaultProductId === null) return; // no product context on the page

  var modal = root.querySelector('[data-cbqr-modal]');
  var form = root.querySelector('[data-cbqr-form]');
  var successPanel = root.querySelector('[data-cbqr-success]');
  var submitBtn = root.querySelector('[data-cbqr-submit]');
  var submitError = root.querySelector('[data-cbqr-submit-error]');
  var companyField = root.querySelector('[data-cbqr-company-field]');
  var lastTrigger = null;

  // The product the modal is currently open for: { id, product, variants, scope }.
  var active = null;

  var STORE = {
    form: 'cbqr:v1:form',
    submissionId: 'cbqr:v1:submission_id',
    sessionId: 'cbqr:v1:session_id',
    attribution: 'cbqr:v1:attribution',
  };

  // ── sessionStorage (guarded — Safari private mode etc. throws) ─────────────
  function ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, value) {
    try { sessionStorage.setItem(key, value); } catch (e) { /* best-effort */ }
  }
  function ssGetJson(key) {
    var raw = ssGet(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  function readJson(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function uuid() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'xxxx-xxxx-xxxx-xxxx'.replace(/x/g, function () {
      return Math.floor(Math.random() * 16).toString(16);
    }) + '-' + Date.now().toString(16);
  }

  function stableId(key) {
    var v = ssGet(key);
    if (!v) { v = uuid(); ssSet(key, v); }
    return v;
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  function singleProduct() {
    return Object.keys(products).length === 1;
  }

  function track(event, data) {
    // Metrics hook: completion rate = opens vs successes; per-field drop-off =
    // field_error counts by field. Wire these into GA4/GTM via dataLayer.
    window.dataLayer = window.dataLayer || [];
    var payload = { event: 'cbqr_' + event, cbqr_product: active ? active.product.handle : null };
    if (data) for (var k in data) payload['cbqr_' + k] = data[k];
    window.dataLayer.push(payload);
  }

  // ── First-touch attribution ────────────────────────────────────────────────
  // Captured on every page load; the FIRST page of the session wins so the ad
  // click that brought the visitor in survives navigation to the PDP.
  (function captureAttribution() {
    if (ssGet(STORE.attribution)) return;
    var params = new URLSearchParams(window.location.search);
    ssSet(STORE.attribution, JSON.stringify({
      landing_page_url: window.location.href,
      referrer: document.referrer || null,
      raw_query_string: window.location.search.replace(/^\?/, '') || null,
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
      gclid: params.get('gclid'),
      gbraid: params.get('gbraid'),
      wbraid: params.get('wbraid'),
      fbclid: params.get('fbclid'),
    }));
  })();

  // ── Which product did the trigger ask for? ─────────────────────────────────
  function resolveProduct(trigger) {
    var id = trigger ? trigger.getAttribute('data-cbqr-open') : null;
    if (id && products[id]) return { id: id, data: products[id] };
    // Bare data-cbqr-open (no value): fall back to the sole/first product block.
    if (products[defaultProductId]) return { id: defaultProductId, data: products[defaultProductId] };
    return null;
  }

  // ── Variant capture (scoped to the clicked card) ───────────────────────────
  // A variant picker keeps a product form's [name=id] current. On a product page
  // that's the one form; on a category page each card may (or may not) have a
  // quick-add form. We only read a form that is unambiguously the active
  // product's — otherwise we fall back to that product's default variant so we
  // never pick up a neighbouring card's selection.
  function productFormsIn(scope) {
    return Array.prototype.filter.call(
      scope.querySelectorAll('form[action*="/cart/add"]'),
      function (f) { return !(f.id && f.id.indexOf('installment') !== -1); }
    );
  }

  // Closest ancestor of the trigger that contains exactly one product form, or
  // document when the whole page has exactly one. null = don't trust any form.
  function resolveScope(trigger) {
    var node = trigger;
    while (node && node !== document.body && node.nodeType === 1) {
      if (node.querySelector && node.querySelector('form[action*="/cart/add"]')) {
        var forms = productFormsIn(node);
        if (forms.length === 1) return node;
        if (forms.length > 1) return null; // spans multiple cards — ambiguous
      }
      node = node.parentElement;
    }
    return productFormsIn(document).length === 1 ? document : null;
  }

  function activeForms() {
    if (!active || !active.scope) return [];
    return productFormsIn(active.scope);
  }

  function currentVariantId() {
    var forms = activeForms();
    for (var i = 0; i < forms.length; i++) {
      var input = forms[i].querySelector('[name="id"]');
      if (input && input.value) return Number(input.value);
    }
    if (singleProduct()) {
      var fromUrl = new URLSearchParams(window.location.search).get('variant');
      if (fromUrl) return Number(fromUrl) || null;
    }
    return Number(active.product.defaultVariantId) || null;
  }

  function currentVariant() {
    var id = currentVariantId();
    var variants = active ? active.variants : [];
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].id === id) return variants[i];
    }
    return null;
  }

  function selectedProperties() {
    var out = {};
    activeForms().forEach(function (f) {
      f.querySelectorAll('[name^="properties["]').forEach(function (el) {
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        if (el.value === '') return;
        var name = el.name.replace(/^properties\[/, '').replace(/\]$/, '');
        out[name] = el.value;
      });
    });
    return out;
  }

  // Named option values ({ Performance: "Residential", ... }) from the variant.
  function selectedOptions(variant) {
    var out = {};
    if (!variant || !active) return out;
    (active.product.optionNames || []).forEach(function (name, i) {
      if (variant.options && variant.options[i] != null) out[name] = variant.options[i];
    });
    return out;
  }

  // Reciprocal pairing labels: {sauna:'Pair with an ice bath', ...}. The label
  // offered is the OPPOSITE of the base product's category.
  var PAIR_LEGEND = { sauna: 'Pair with an ice bath', ice_bath: 'Pair with a sauna' };

  // Fill the locked product-context header + reciprocal pairing from the active
  // product. Sauna base → offer ice baths; ice-bath base → offer saunas; a base
  // in neither list → hide the whole pairing section.
  function applyActiveProduct() {
    var p = active.product;
    var img = root.querySelector('[data-cbqr-product-img]');
    if (img) {
      if (p.image) { img.src = p.image; img.alt = p.title || ''; img.hidden = false; }
      else { img.removeAttribute('src'); img.hidden = true; }
    }
    var titleEl = root.querySelector('[data-cbqr-product-title]');
    if (titleEl) titleEl.textContent = p.title || '';

    var cat = p.category; // 'sauna' | 'ice_bath' | null
    var legend = root.querySelector('[data-cbqr-pair-legend]');
    if (legend) legend.textContent = PAIR_LEGEND[cat] || '';

    var none = form.querySelector('[name="cbqr-pair"][value=""]');
    var anyVisible = false;
    Array.prototype.forEach.call(root.querySelectorAll('[data-cbqr-pair-for]'), function (label) {
      // Show an option when it pairs with THIS base category and isn't the base
      // product itself (guards the odd case of a product listed in both roles).
      var sameProduct = String(label.getAttribute('data-cbqr-addon-id')) === String(active.id);
      var show = !!cat && label.getAttribute('data-cbqr-pair-for') === cat && !sameProduct;
      label.hidden = !show;
      if (show) { anyVisible = true; return; }
      var input = label.querySelector('input');
      if (input && input.checked) {
        input.checked = false;
        if (none) none.checked = true; // a hidden pick falls back to "No thanks"
      }
    });

    var section = root.querySelector('[data-cbqr-addons]');
    if (section) section.hidden = !anyVisible;
  }

  function refreshProductContext() {
    var variant = currentVariant();
    var label = root.querySelector('[data-cbqr-variant-label]');
    if (label) label.textContent = variant ? variant.title : '';
    var props = selectedProperties();
    var keys = Object.keys(props);
    var propsEl = root.querySelector('[data-cbqr-props]');
    if (propsEl) {
      propsEl.hidden = keys.length === 0;
      propsEl.textContent = keys.map(function (k) { return k + ': ' + props[k]; }).join(' · ');
    }
  }

  // ── Modal open / close / focus trap ────────────────────────────────────────
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // After a successful submit the success panel is showing; reopening (common on
  // a category page, for a second product) should start on a fresh form.
  function resetToForm() {
    form.hidden = false;
    successPanel.hidden = true;
    submitError.hidden = true;
  }

  function openModal(trigger) {
    var resolved = resolveProduct(trigger);
    if (!resolved) return;
    lastTrigger = trigger || null;
    active = {
      id: resolved.id,
      product: resolved.data.product,
      variants: resolved.data.variants || [],
      scope: resolveScope(trigger),
    };
    resetToForm();
    refreshProductContext();
    restoreForm();
    applyActiveProduct(); // after restore, so it wins over any stale add-on selection
    root.hidden = false;
    document.body.classList.add('cbqr-modal-open');
    var first = form.querySelector('input, select, textarea');
    if (first) first.focus();
    track('open');
  }

  function closeModal() {
    root.hidden = true;
    document.body.classList.remove('cbqr-modal-open');
    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
  }

  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-cbqr-open]');
    if (opener) { e.preventDefault(); openModal(opener); }
  });
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-cbqr-close]') || e.target === root) closeModal();
  });
  document.addEventListener('keydown', function (e) {
    if (root.hidden) return;
    if (e.key === 'Escape') { closeModal(); return; }
    if (e.key !== 'Tab') return;
    var focusable = Array.prototype.filter.call(
      modal.querySelectorAll(FOCUSABLE),
      function (el) { return el.offsetParent !== null; }
    );
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  // ── Form persistence ───────────────────────────────────────────────────────
  var PERSISTED = ['first_name', 'last_name', 'email', 'phone', 'customer_type',
                   'postcode', 'company_name', 'timeline', 'notes'];

  function saveForm() {
    var data = {};
    PERSISTED.forEach(function (name) {
      var el = form.elements[name];
      if (el) data[name] = el.value;
    });
    data.marketing_consent = form.elements.marketing_consent.checked;
    var pick = form.querySelector('[name="cbqr-pair"]:checked');
    data.pair = pick ? pick.value : '';
    ssSet(STORE.form, JSON.stringify(data));
  }

  function restoreForm() {
    var data = ssGetJson(STORE.form);
    if (!data) return;
    PERSISTED.forEach(function (name) {
      var el = form.elements[name];
      if (el && data[name] != null) el.value = data[name];
    });
    form.elements.marketing_consent.checked = !!data.marketing_consent;
    // The pairing pick is re-validated against the active product's category by
    // applyActiveProduct() (called after restore), which clears it if hidden.
    if (data.pair) {
      var pick = form.querySelector('[name="cbqr-pair"][value="' + CSS.escape(data.pair) + '"]');
      if (pick) pick.checked = true;
    }
    toggleCompanyField();
  }

  form.addEventListener('input', saveForm);
  form.addEventListener('change', saveForm);

  // ── Company field: shown + required only for commercial ────────────────────
  function toggleCompanyField() {
    var commercial = form.elements.customer_type.value === 'commercial';
    companyField.hidden = !commercial;
    form.elements.company_name.required = commercial;
    if (!commercial) clearError(form.elements.company_name);
  }
  form.elements.customer_type.addEventListener('change', toggleCompanyField);

  // ── Validation (inline on blur; all-fields on submit) ──────────────────────
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


  // function validPhoneAU(v) {
  //   var d = v.replace(/[^\d+]/g, '');
  //   // Mobiles + landlines (+61 or 0 prefixed) and 13/1300/1800 business numbers.
  //   return /^(?:\+?61[2-478]\d{8}|0[2-478]\d{8}|1[38]00\d{6}|13\d{4})$/.test(d);
  // }

//   function validInternationalPhone(phone) {
//     try {
//         const phoneNumber = libphonenumber.parsePhoneNumber(phone,'AU');
//         return phoneNumber && phoneNumber.isValid();
//     } catch (e) {
//         return false;
//     }
// }

function validInternationalPhone(phone) {
    try {
        if (!window.libphonenumber || !libphonenumber.parsePhoneNumber) { var digits = String(phone).replace(/[^\d+]/g, ''); return /^\+?\d{8,15}$/.test(digits); } const phoneNumber = libphonenumber.parsePhoneNumber(phone, 'AU');
        return phoneNumber && phoneNumber.isValid();
    } catch (e) {
        return false;
    }
}

  var VALIDATORS = {
    first_name: function (v) { return v.trim() ? null : 'Please enter your first name.'; },
    last_name: function (v) { return v.trim() ? null : 'Please enter your last name — we need it for the quote document.'; },
    email: function (v) {
      if (!v.trim()) return 'Please enter your email.';
      return EMAIL_RE.test(v.trim()) ? null : 'That email doesn’t look right.';
    },

    // phone: function (v) {
    //   if (!v.trim()) return 'Please enter your phone number.';
    //   return validPhoneAU(v) ? null : 'Please enter an Australian phone number (e.g. 04xx xxx xxx).';
    // },

     phone: function (v) {
      if (!v.trim()) return 'Please enter your phone number.';
      return validInternationalPhone(v) ? null : 'Please enter a registered phone number.';
    },

    customer_type: function (v) { return v ? null : 'Please tell us the customer type.'; },
    postcode: function (v) {
      if (!v.trim()) return 'We need your postcode to price freight.';
      return /^\d{4}$/.test(v.trim()) ? null : 'Please enter a 4-digit Australian postcode.';
    },
    company_name: function (v, el) {
      if (!el.required) return null;
      return v.trim() ? null : 'Please enter your company name.';
    },
  };

  function errorElFor(el) { return document.getElementById(el.id + '-error'); }

  function showError(el, message) {
    var err = errorElFor(el);
    el.setAttribute('aria-invalid', 'true');
    if (err) {
      err.textContent = message;
      err.hidden = false;
      el.setAttribute('aria-describedby', err.id);
    }
  }

  function clearError(el) {
    var err = errorElFor(el);
    el.removeAttribute('aria-invalid');
    if (err) { err.hidden = true; err.textContent = ''; }
  }

  function validateField(el) {
    var check = VALIDATORS[el.name];
    if (!check) return true;
    var message = check(el.value, el);
    if (message) { showError(el, message); return false; }
    clearError(el);
    return true;
  }

  form.addEventListener('focusout', function (e) {
    var el = e.target;
    if (el.name && VALIDATORS[el.name] && el.value !== '') {
      if (!validateField(el)) track('field_error', { field: el.name });
    }
  });
  form.addEventListener('input', function (e) {
    // Clear an error as soon as the fix makes the field valid again.
    var el = e.target;
    if (el.name && VALIDATORS[el.name] && el.getAttribute('aria-invalid')) validateField(el);
  });

  function validateAll() {
    var firstBad = null;
    Object.keys(VALIDATORS).forEach(function (name) {
      var el = form.elements[name];
      if (!el) return;
      if (!validateField(el) && !firstBad) firstBad = el;
    });
    return firstBad;
  }

  // ── Payload ────────────────────────────────────────────────────────────────
  function collectAddOns() {
    var addOns = [];
    var pick = form.querySelector('[name="cbqr-pair"]:checked');
    if (pick && pick.value) {
      addOns.push({
        kind: pick.dataset.kind || 'pair', // 'sauna' | 'ice_bath'
        handle: pick.value,
        variant_id: Number(pick.dataset.variantId) || null,
        title: pick.dataset.title || null,
      });
    }
    return addOns;
  }

  function buildPayload() {
    var submittedAt = new Date().toISOString();
    var variant = currentVariant();
    var options = selectedOptions(variant);
    var properties = selectedProperties();
    var addOns = collectAddOns();
    var attribution = ssGetJson(STORE.attribution) || {};
    var consent = form.elements.marketing_consent.checked;
    var f = function (name) { return form.elements[name].value.trim(); };

    // A human-readable one-liner for the Leads list's product column.
    var interest = active.product.title + (variant ? ' — ' + variant.title : '');
    addOns.forEach(function (a) { if (a.title) interest += ' + ' + a.title; });

    // The full structured request — preserved untouched in raw_lead_payload
    // (the schema is passthrough), ready for the server-side deal pass.
    var quoteRequest = {
      base_product: {
        handle: active.product.handle,
        variant_id: variant ? variant.id : currentVariantId(),
        title: interest.split(' + ')[0],
        url: active.product.url,
        display_price_cents: variant ? variant.price : null, // display only — server must reprice
      },
      selected_options: options,
      line_item_properties: properties,
      add_ons: addOns,
      customer_type: f('customer_type'),
      company_name: f('company_name') || null,
      timeline: f('timeline') || null,
      notes: f('notes') || null,
      submitted_at: submittedAt,
    };

    return {
      external_id: 'shopify-quote:' + stableId(STORE.submissionId),
      platform: 'Shopify',
      form_name: 'quote_request',
      lead_date: submittedAt,
      first_name: f('first_name'),
      last_name: f('last_name'),
      email: f('email'),
      phone: f('phone'),
      postcode: f('postcode'),
      country_code: 'AU',
      residential_commercial: f('customer_type') === 'commercial' ? 'Commercial' : 'Residential',
      product_interest: interest,
      session_id: stableId(STORE.sessionId),
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_referrer: attribution.referrer || document.referrer || null,
      landing_page_url: attribution.landing_page_url || null,
      raw_query_string: attribution.raw_query_string || null,
      utm_source: attribution.utm_source || null,
      utm_medium: attribution.utm_medium || null,
      utm_campaign: attribution.utm_campaign || null,
      utm_content: attribution.utm_content || null,
      utm_term: attribution.utm_term || null,
      gclid: attribution.gclid || null,
      gbraid: attribution.gbraid || null,
      wbraid: attribution.wbraid || null,
      fbclid: attribution.fbclid || null,
      fbp: cookie('_fbp'),
      fbc: cookie('_fbc'),
      ga_client_id: (function () {
        var ga = cookie('_ga'); // GA1.1.123456789.1700000000 → 123456789.1700000000
        return ga ? ga.split('.').slice(-2).join('.') : null;
      })(),
      user_agent: navigator.userAgent,
      marketing_consent: consent ? true : null,
      // The ad-platform consents, sent from the same tick. marketing_consent
      // alone satisfies Meta and the coarse upload_readiness gate, but Google
      // Data Manager requires ad_user_data_consent SPECIFICALLY — without it a
      // quote request reads "Ready" in the admin and is silently refused by
      // Google forever. The tick's notice wording is what authorises these.
      ad_user_data_consent: consent ? true : null,
      ad_personalization_consent: consent ? true : null,
      consent_timestamp: consent ? submittedAt : null,
      quote_request: quoteRequest,
    };
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  form.addEventListener('submit', function (e) {
    e.preventDefault();
    submitError.hidden = true;

    var firstBad = validateAll();
    if (firstBad) { firstBad.focus(); return; }

    var payload = buildPayload();
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending…';
    track('submit_attempt', { add_ons: payload.quote_request.add_ons.length });

    fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Api-Key': config.apiKey },
      body: JSON.stringify(payload),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function () {
        // Success: rotate the submission id so the NEXT request is a new lead,
        // and clear the saved form so reopening starts fresh.
        try { sessionStorage.removeItem(STORE.submissionId); } catch (err) { /* noop */ }
        try { sessionStorage.removeItem(STORE.form); } catch (err) { /* noop */ }
        var copy = successPanel.querySelector('[data-cbqr-success-copy]');
        if (copy) {
          copy.textContent = 'Thanks ' + payload.first_name +
            ' — we’ve got your request and will email your quote to ' + payload.email + '.';
        }
        form.hidden = true;
        successPanel.hidden = false;
        successPanel.querySelector(FOCUSABLE).focus();
        track('submit_success', {
          customer_type: payload.quote_request.customer_type,
          timeline: payload.quote_request.timeline || 'unset',
          pair_attached: payload.quote_request.add_ons.length > 0,
          pair_kind: (payload.quote_request.add_ons[0] || {}).kind || 'none',
        });
      })
      .catch(function () {
        // The submission id is kept, so this retry can never duplicate the lead.
        submitError.textContent = 'Something went wrong sending your request. Please try again — your details are saved.';
        submitError.hidden = false;
        track('submit_error');
      })
      .then(function () {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Request my quote';
      });
  });
})();
